// src/pages/ChiffreAffaires.jsx
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Form, Badge, Dropdown, Modal } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, nettoyerMontant } from "../utils/formatters";
import { exportCAtoExcel, exportCAtoCSV } from "../utils/excelExport";
import { generateCAPDF } from "../utils/pdfExport";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";

export default function ChiffreAffaires() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [viewMode, setViewMode] = useState('mensuel');
    const [chartLoading, setChartLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);

    useEffect(() => {
        fetchChiffreAffaires();
    }, [selectedYear, selectedMonth]);

    /* ============================================================
       EXPORTS
       ============================================================ */
    const handleExportPDF = async () => {
        setPdfLoading(true);
        setShowPdfModal(true);
        try {
            await generateCAPDF(data, selectedYear);
        } catch (error) {
            console.error("Erreur PDF:", error);
            alert("Erreur lors de la génération du PDF");
        } finally {
            setPdfLoading(false);
            setTimeout(() => setShowPdfModal(false), 1000);
        }
    };

    const handleExportExcel = () => {
        try {
            exportCAtoExcel(data, selectedYear, selectedMonth);
        } catch (error) {
            console.error("Erreur Excel:", error);
            alert("Erreur lors de l'export Excel");
        }
    };

    const handleExportCSV = () => {
        try {
            exportCAtoCSV(data, selectedYear);
        } catch (error) {
            console.error("Erreur CSV:", error);
            alert("Erreur lors de l'export CSV");
        }
    };

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchChiffreAffaires = async () => {
        try {
            setLoading(true);
            setChartLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/ca.php?annee=${selectedYear}&mois=${selectedMonth}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur de chargement des données");
            const result = await response.json();
            setData(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setChartLoading(false);
        }
    };

    const handleYearChange = (e) => setSelectedYear(parseInt(e.target.value));
    const handleMonthChange = (e) => setSelectedMonth(parseInt(e.target.value));

    const getMonthName = (month) => {
        const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
        return months[month - 1];
    };

    /* ============================================================
       CALCULS
       ============================================================ */
    const caMensuel = data?.annuel?.find(m => m.mois === selectedMonth)?.total_ttc || 0;
    const caMensuelNettoye = nettoyerMontant(caMensuel);
    const caJournalier = data?.journalier?.total_ttc || 0;
    const nbFacturesJour = data?.journalier?.nb_factures || 0;
    const panierMoyen = nbFacturesJour > 0 ? caJournalier / nbFacturesJour : 0;
    const caAnnuel = data?.annuel?.reduce((acc, m) => {
        const montant = typeof m.total_ttc === 'number' ? m.total_ttc : parseFloat(String(m.total_ttc).replace(/[^\d.-]/g, '')) || 0;
        return acc + montant;
    }, 0) || 0;
    const tvaAnnuelle = data?.annuel?.reduce((acc, m) => acc + (m.total_tva || 0), 0) || 0;
    const regleAnnuel = data?.annuel?.reduce((acc, m) => acc + (m.total_regle || 0), 0) || 0;
    const tauxEncaissement = (caAnnuel && caAnnuel > 0) ? ((regleAnnuel || 0) / caAnnuel) * 100 : 0;

    /* ============================================================
       CHART OPTIONS
       ============================================================ */
    const getChartOptions = (type) => {
        const baseOptions = {
            chart: {
                toolbar: { show: true, tools: { download: true } },
                zoom: { enabled: true },
                fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
                background: 'transparent'
            },
            grid: { borderColor: '#e9ecef', strokeDashArray: 5, position: 'back' },
            dataLabels: { enabled: false },
            colors: [COLORS.primary, COLORS.secondary, '#f59e0b', '#ef4444', '#8b5cf6'],
            tooltip: {
                theme: 'dark',
                y: { formatter: (value) => formatCurrency(value), title: { formatter: (seriesName) => `${seriesName}: ` } },
                style: { fontSize: '12px' }
            },
            legend: { position: 'top', horizontalAlign: 'center', markers: { radius: 12 } }
        };

        if (type === 'mensuel') {
            return {
                ...baseOptions,
                chart: { ...baseOptions.chart, type: 'line' },
                xaxis: { categories: Array.from({ length: 31 }, (_, i) => i + 1), title: { text: 'Jour du mois', style: { fontWeight: 500 } }, labels: { rotate: -45 } },
                yaxis: { title: { text: 'Montant (FCFA)' }, labels: { formatter: (value) => formatCurrency(value) } },
                stroke: { curve: 'smooth', width: 3, dashArray: 0 },
                fill: { type: 'gradient', gradient: { shadeIntensity: 0.5, opacityFrom: 0.7, opacityTo: 0.9 } }
            };
        }

        if (type === 'annuel') {
            return {
                ...baseOptions,
                chart: { ...baseOptions.chart, type: 'bar' },
                xaxis: { categories: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'], title: { text: 'Mois' } },
                yaxis: { title: { text: 'Montant (FCFA)' }, labels: { formatter: (value) => formatCurrency(value) } },
                plotOptions: { bar: { columnWidth: '55%', borderRadius: 8, distributed: false } }
            };
        }

        if (type === 'evolution') {
            return {
                ...baseOptions,
                chart: { ...baseOptions.chart, type: 'area' },
                xaxis: { categories: data?.evolution?.map(item => item.annee) || [], title: { text: 'Année' } },
                yaxis: { title: { text: 'Montant (FCFA)' }, labels: { formatter: (value) => formatCurrency(value) } },
                fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.6, opacityTo: 0.1 } }
            };
        }

        return baseOptions;
    };

    const seriesMensuel = [{ name: 'CA Journalier', data: data?.mensuel_detail?.map(item => item.ca_journalier) || Array(31).fill(0) }];
    const seriesAnnuel = [{ name: 'CA Mensuel', data: data?.annuel?.map(item => item.total_ttc) || Array(12).fill(0) }];
    const seriesEvolution = [{ name: 'CA Annuel', data: data?.evolution?.map(item => item.total_ttc) || [] }];
    const seriesComparaison = [
        { name: `${selectedYear}`, data: data?.annuel?.map(item => item.total_ttc) || Array(12).fill(0) },
        { name: `${selectedYear - 1}`, data: data?.annee_precedente?.map(item => item.total_ttc) || Array(12).fill(0) }
    ];

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-bar-chart-2-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des données financières
                        </h5>
                        <p className="text-muted small">Analyse du chiffre d'affaires en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={() => window.location.reload()} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />

            {/* ===== MODAL PDF ===== */}
            <Modal show={showPdfModal} centered backdrop="static" keyboard={false} className="modal-charte">
                <Modal.Body className="text-center py-5 px-4">
                    <div className="pdf-loader-charte">
                        <img src={logo} alt="Logo" className="pdf-loader-logo-charte" />
                        <div className="pdf-loader-spinner-charte">
                            <i className="ri-loader-4-line"></i>
                        </div>
                        <h5 className="mt-3 fw-bold" style={{ color: COLORS.primary }}>
                            Génération du PDF en cours...
                        </h5>
                        <p className="text-muted small mb-0">Veuillez patienter quelques instants</p>
                        <div className="progress-pdf-charte">
                            <div className="progress-fill-pdf-charte"></div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-bar-chart-2-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Analyse du Chiffre d'Affaires</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-money-euro-circle-line"></i> CA annuel {formatCurrency(caAnnuel)}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-calendar-line"></i> {getMonthName(selectedMonth)} {selectedYear} : {formatCurrency(caMensuelNettoye)}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-line-chart-line"></i> Encaissement {isNaN(tauxEncaissement) ? '0.0' : tauxEncaissement.toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                        </div>
                        <Dropdown align="end">
                            <Dropdown.Toggle className="btn-modern-outline" disabled={pdfLoading}>
                                <i className="ri-download-line me-1"></i> Exporter
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="dropdown-menu-charte">
                                <Dropdown.Item onClick={handleExportPDF} disabled={pdfLoading}>
                                    <i className="ri-file-pdf-line me-2" style={{ color: COLORS.danger }}></i> PDF
                                </Dropdown.Item>
                                <Dropdown.Item onClick={handleExportExcel}>
                                    <i className="ri-file-excel-line me-2" style={{ color: COLORS.success }}></i> Excel
                                </Dropdown.Item>
                                <Dropdown.Item onClick={handleExportCSV}>
                                    <i className="ri-file-csv-line me-2" style={{ color: COLORS.info }}></i> CSV
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="auto">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-filter-3-line" style={{ color: 'white' }}></i>
                                        <span className="small fw-semibold" style={{ color: 'white' }}>FILTRES :</span>
                                    </div>
                                </Col>
                                <Col md="auto">
                                    <Form.Select
                                        value={selectedYear}
                                        onChange={handleYearChange}
                                        className="filter-select-charte"
                                        style={{ minWidth: '120px' }}
                                    >
                                        {[2030, 2029, 2028, 2027, 2026, 2025, 2024, 2023, 2022].map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md="auto">
                                    <Form.Select
                                        value={selectedMonth}
                                        onChange={handleMonthChange}
                                        className="filter-select-charte"
                                        style={{ minWidth: '150px' }}
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                            <option key={month} value={month}>{getMonthName(month)}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col className="ms-auto">
                                    <div className="view-selector-charte">
                                        {[
                                            { mode: 'mensuel', label: 'Journalier', icon: 'ri-calendar-line' },
                                            { mode: 'annuel', label: 'Mensuel', icon: 'ri-bar-chart-line' },
                                            { mode: 'comparaison', label: 'Comparaison', icon: 'ri-git-compare-line' }
                                        ].map(({ mode, label, icon }) => (
                                            <button
                                                key={mode}
                                                className={`view-btn-charte ${viewMode === mode ? 'active' : ''}`}
                                                onClick={() => setViewMode(mode)}
                                            >
                                                <i className={icon}></i>
                                                <span>{label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">CA du jour</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(caJournalier)}</h3>
                                    <span className="kpi-unit-charte">{nbFacturesJour} factures</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-calendar-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">CA du mois</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(caMensuelNettoye)}</h3>
                                    <span className="kpi-unit-charte">{getMonthName(selectedMonth)} {selectedYear}</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-bar-chart-2-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">CA annuel</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(caAnnuel)}</h3>
                                    <span className="kpi-unit-charte">Année {selectedYear}</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-line-chart-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Taux encaissement</span>
                                    <h3 className="kpi-value-charte">{isNaN(tauxEncaissement) ? '0.0' : tauxEncaissement.toFixed(1)}%</h3>
                                    <span className="kpi-unit-charte">sur {formatCurrency(caAnnuel)}</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== GRAPHIQUES ===== */}
                    <Row className="g-3 mb-4">
                        <Col xl="8">
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-bar-chart-2-line text-white"></i>
                                        <h6 className="mb-0 fw-semibold text-white">
                                            {viewMode === 'mensuel' && `CA Journalier - ${getMonthName(selectedMonth)} ${selectedYear}`}
                                            {viewMode === 'annuel' && `CA Mensuel - Année ${selectedYear}`}
                                            {viewMode === 'comparaison' && `Évolution du CA sur 5 ans`}
                                        </h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div style={{ position: 'relative', minHeight: '400px' }}>
                                        {chartLoading ? (
                                            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                                                <Spinner animation="border" style={{ color: COLORS.primary }} />
                                            </div>
                                        ) : (
                                            <>
                                                {viewMode === 'mensuel' && <ReactApexChart series={seriesMensuel} options={getChartOptions('mensuel')} type="line" height={400} />}
                                                {viewMode === 'annuel' && <ReactApexChart series={seriesAnnuel} options={getChartOptions('annuel')} type="bar" height={400} />}
                                                {viewMode === 'comparaison' && <ReactApexChart series={seriesEvolution} options={getChartOptions('evolution')} type="area" height={400} />}
                                            </>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl="4">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient-secondary">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-pie-chart-2-line text-white"></i>
                                        <h6 className="mb-0 fw-semibold text-white">Répartition des règlements</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    {data?.reglements?.length > 0 ? (
                                        (() => {
                                            const modesMap = { 'especes': 'Espèces', 'cheque': 'Chèque', 'virement': 'Virement', 'carte_bancaire': 'Carte bancaire', 'traite': 'Traite' };
                                            let donutLabels = data.reglements.map(item => modesMap[item.mode_reglement] || item.mode_reglement);
                                            let donutSeries = data.reglements.map(item => Math.max(item.total, 0.01));
                                            const total = donutSeries.reduce((a, b) => a + b, 0);
                                            if (total > 0) {
                                                const filteredData = [];
                                                const otherData = { label: 'Autres', value: 0 };
                                                donutSeries.forEach((value, index) => {
                                                    const percentage = (value / total) * 100;
                                                    if (percentage >= 5) filteredData.push({ label: donutLabels[index], value });
                                                    else otherData.value += value;
                                                });
                                                if (otherData.value > 0) filteredData.push(otherData);
                                                donutLabels = filteredData.map(item => item.label);
                                                donutSeries = filteredData.map(item => item.value);
                                            }
                                            const donutOptions = {
                                                chart: { type: 'donut', height: 320, fontFamily: 'Inter, system-ui, sans-serif', toolbar: { show: false } },
                                                labels: donutLabels,
                                                colors: [COLORS.primary, COLORS.secondary, '#06b6d4', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
                                                legend: { position: 'bottom', fontSize: '11px', offsetY: 5, markers: { radius: 8 } },
                                                tooltip: { y: { formatter: (value) => formatCurrency(value) } },
                                                plotOptions: {
                                                    pie: {
                                                        donut: {
                                                            size: '65%',
                                                            labels: {
                                                                show: true,
                                                                total: {
                                                                    show: true,
                                                                    label: 'Total',
                                                                    fontSize: '14px',
                                                                    fontWeight: 600,
                                                                    color: COLORS.primaryDark,
                                                                    formatter: () => formatCurrency(donutSeries.reduce((a, b) => a + b, 0))
                                                                }
                                                            }
                                                        }
                                                    }
                                                },
                                                dataLabels: { style: { fontSize: '11px', fontWeight: 500 } },
                                                stroke: { show: false }
                                            };
                                            return <ReactApexChart series={donutSeries} options={donutOptions} type="donut" height={320} />;
                                        })()
                                    ) : (
                                        <div className="empty-state-charte">
                                            <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.secondary}15, ${COLORS.secondary}05)`, color: COLORS.secondary }}>
                                                <i className="ri-pie-chart-2-line"></i>
                                            </div>
                                            <h6 className="fw-semibold mb-1">Aucune donnée</h6>
                                            <p className="text-muted small mb-0">Aucune donnée de règlement</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== COMPARAISON ===== */}
                    {viewMode === 'comparaison' && (
                        <Row className="g-3 mb-4">
                            <Col xl="12">
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="card-header-gradient">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-git-compare-line text-white"></i>
                                            <h6 className="mb-0 fw-semibold text-white">
                                                Comparaison {selectedYear} vs {selectedYear - 1}
                                            </h6>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-3">
                                        <div style={{ minHeight: '350px' }}>
                                            {chartLoading ? (
                                                <div className="d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                                                    <Spinner animation="border" style={{ color: COLORS.primary }} />
                                                </div>
                                            ) : (
                                                <ReactApexChart
                                                    series={seriesComparaison}
                                                    options={{ ...getChartOptions('annuel'), colors: [COLORS.primary, '#f59e0b'] }}
                                                    type="line"
                                                    height={350}
                                                />
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* ===== TABLEAUX DÉTAILLÉS ===== */}
                    <Row className="g-3 mb-4">
                        <Col xl="8">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-table-line text-white"></i>
                                        <h6 className="mb-0 fw-semibold text-white">Détail mensuel {selectedYear}</h6>
                                    </div>
                                    <span className="badge-header-light">12 mois</span>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0 table-charte">
                                            <thead>
                                                <tr>
                                                    <th className="ps-4">Mois</th>
                                                    <th className="text-end">Factures</th>
                                                    <th className="text-end">Clients</th>
                                                    <th className="text-end">HT</th>
                                                    <th className="text-end">TVA</th>
                                                    <th className="text-end">TTC</th>
                                                    <th className="text-end pe-4">Réglé</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data?.annuel?.map((mois) => (
                                                    <tr key={mois.mois}>
                                                        <td className="ps-4 fw-semibold" style={{ color: COLORS.primaryDark }}>
                                                            {getMonthName(mois.mois)}
                                                        </td>
                                                        <td className="text-end">{formatNumber(mois.nb_factures || 0)}</td>
                                                        <td className="text-end">{formatNumber(mois.nb_clients || 0)}</td>
                                                        <td className="text-end" style={{ color: COLORS.primary }}>{formatCurrency(mois.total_ht || 0)}</td>
                                                        <td className="text-end" style={{ color: COLORS.warning }}>{formatCurrency(mois.total_tva || 0)}</td>
                                                        <td className="text-end fw-bold" style={{ color: COLORS.primaryDark }}>{formatCurrency(mois.total_ttc || 0)}</td>
                                                        <td className="text-end pe-4" style={{ color: COLORS.success }}>{formatCurrency(mois.total_regle || 0)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td className="ps-4 fw-bold">TOTAL</td>
                                                    <td className="text-end fw-bold">{formatNumber(data?.annuel?.reduce((acc, m) => acc + (m.nb_factures || 0), 0))}</td>
                                                    <td className="text-end fw-bold">—</td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.primary }}>{formatCurrency(data?.annuel?.reduce((acc, m) => acc + (m.total_ht || 0), 0))}</td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.warning }}>{formatCurrency(data?.annuel?.reduce((acc, m) => acc + (m.total_tva || 0), 0))}</td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.primaryDark }}>{formatCurrency(caAnnuel)}</td>
                                                    <td className="text-end pe-4 fw-bold" style={{ color: COLORS.success }}>{formatCurrency(regleAnnuel)}</td>
                                                </tr>
                                            </tfoot>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl="4">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient-secondary">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-line-chart-line text-white"></i>
                                        <h6 className="mb-0 fw-semibold text-white">Prévisions</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <div className="prev-item-charte">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="prev-label-charte">Mois prochain</span>
                                            <span className="prev-value-charte" style={{ color: COLORS.primary }}>
                                                {formatCurrency(data?.previsions?.prevision_mois_prochain || 0)}
                                            </span>
                                        </div>
                                        <div className="prev-bar-charte">
                                            <div className="prev-bar-fill-charte" style={{ width: '75%', background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}></div>
                                        </div>
                                    </div>
                                    <div className="prev-item-charte">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="prev-label-charte">Trimestre</span>
                                            <span className="prev-value-charte" style={{ color: COLORS.secondary }}>
                                                {formatCurrency(data?.previsions?.prevision_trimestre || 0)}
                                            </span>
                                        </div>
                                        <div className="prev-bar-charte">
                                            <div className="prev-bar-fill-charte" style={{ width: '85%', background: `linear-gradient(90deg, ${COLORS.secondary}, #0d8ba6)` }}></div>
                                        </div>
                                    </div>
                                    <div className="prev-item-charte">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="prev-label-charte">Année</span>
                                            <span className="prev-value-charte" style={{ color: COLORS.success }}>
                                                {formatCurrency(data?.previsions?.prevision_annuelle || 0)}
                                            </span>
                                        </div>
                                        <div className="prev-bar-charte">
                                            <div className="prev-bar-fill-charte" style={{ width: '60%', background: `linear-gradient(90deg, ${COLORS.success}, #059669)` }}></div>
                                        </div>
                                    </div>
                                    <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                                    <div className="info-note-charte">
                                        <i className="ri-information-line"></i>
                                        <small>Prévisions basées sur la moyenne des 3 derniers mois</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl="12">
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient-secondary">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-trophy-line text-white"></i>
                                        <h6 className="mb-0 fw-semibold text-white">Top 5 Clients</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0 table-charte">
                                            <thead>
                                                <tr>
                                                    <th className="ps-4">Client</th>
                                                    <th className="text-end">Chiffre d'affaires (CA)</th>
                                                    <th className="text-end pe-4">Factures</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data?.top_clients?.slice(0, 5).map((client) => (
                                                    <tr key={client.id_client}>
                                                        <td className="ps-4">
                                                            <div>
                                                                <span className="fw-semibold" style={{ color: COLORS.primaryDark }}>
                                                                    {client.raison_sociale}
                                                                </span>
                                                                <br />
                                                                <small className="text-muted">{client.code_client}</small>
                                                            </div>
                                                        </td>
                                                        <td className="text-end fw-bold" style={{ color: COLORS.secondary }}>
                                                            {formatCurrency(client.total_ttc)}
                                                        </td>
                                                        <td className="text-end pe-4">
                                                            <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.secondary}, #0d8ba6)` }}>
                                                                {client.nb_factures}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .view-selector-charte {
                    display: inline-flex;
                    background: rgba(255,255,255,0.15);
                    border-radius: 50px;
                    padding: 4px;
                }
                .view-btn-charte {
                    border: none; background: transparent;
                    padding: 0.5rem 1rem;
                    border-radius: 50px;
                    font-size: 0.8rem; font-weight: 500;
                    color: rgba(255,255,255,0.7);
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .view-btn-charte:hover { color: white; }
                .view-btn-charte.active {
                    background: white; color: var(--primary);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== HEADERS GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .card-header-gradient-secondary {
                    background: linear-gradient(135deg, var(--secondary) 0%, #0d8ba6 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tfoot td {
                    background: #f8fafc;
                    padding: 0.85rem 0.75rem;
                    border-top: 2px solid #e2e8f0;
                    font-size: 0.85rem;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 700;
                    min-width: 32px; justify-content: center;
                }

                /* ===== PRÉVISIONS ===== */
                .prev-item-charte { margin-bottom: 1.25rem; }
                .prev-item-charte:last-of-type { margin-bottom: 0; }
                .prev-label-charte {
                    font-size: 0.8rem; color: #64748b; font-weight: 500;
                }
                .prev-value-charte {
                    font-size: 0.85rem; font-weight: 700;
                }
                .prev-bar-charte {
                    height: 6px; border-radius: 4px;
                    background: #f1f5f9;
                    overflow: hidden;
                }
                .prev-bar-fill-charte {
                    height: 100%; border-radius: 4px;
                    transition: width 0.6s ease;
                }
                .info-note-charte {
                    display: flex; align-items: center; gap: 8px;
                    justify-content: center;
                    color: #64748b;
                }
                .info-note-charte i { color: var(--primary); font-size: 1rem; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 80px; height: 80px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 2rem;
                }

                /* ===== DROPDOWN ===== */
                .dropdown-menu-charte {
                    border: none; border-radius: 12px;
                    box-shadow: 0 12px 32px rgba(0,0,0,0.12);
                    padding: 0.5rem;
                }
                .dropdown-menu-charte .dropdown-item {
                    border-radius: 8px;
                    padding: 0.5rem 0.75rem;
                    font-size: 0.85rem;
                    transition: all 0.2s ease;
                }
                .dropdown-menu-charte .dropdown-item:hover { background: #f1f5f9; }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-outline:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTE ERREUR ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== PDF LOADER ===== */
                .pdf-loader-charte {
                    display: flex; flex-direction: column;
                    align-items: center; justify-content: center;
                }
                .pdf-loader-logo-charte {
                    width: 80px; height: 80px;
                    object-fit: contain;
                    border-radius: 50%;
                    margin-bottom: 10px;
                    animation: pulse-logo 1.5s ease-in-out infinite;
                }
                @keyframes pulse-logo {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.05); opacity: 0.85; }
                }
                .pdf-loader-spinner-charte {
                    width: 40px; height: 40px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}15);
                    color: var(--primary);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem;
                    margin-top: 0.5rem;
                }
                .pdf-loader-spinner-charte i {
                    animation: spin 1s linear infinite;
                }
                .progress-pdf-charte {
                    width: 200px; height: 4px;
                    background: #e2e8f0;
                    border-radius: 4px;
                    overflow: hidden;
                    margin-top: 20px;
                }
                .progress-fill-pdf-charte {
                    width: 60%; height: 100%;
                    background: linear-gradient(90deg, var(--primary), var(--secondary));
                    border-radius: 4px;
                    animation: pdf-loading 1.2s ease-in-out infinite;
                }
                @keyframes pdf-loading {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(200%); }
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .view-selector-charte { flex-wrap: wrap; }
                    .view-btn-charte { font-size: 0.7rem; padding: 0.4rem 0.7rem; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}